import {Env} from "@morningtrain/helpers";
import {toQueryString} from "./url";
import {observable} from "mobx";
import {Operation} from "@morningtrain/resources";

class RouterHelper {

    definitions = [];
    components = {};

    constructor() {
        this.current = observable.object({
            route: this.currentRoute,
            parameters: this.currentParameters,
        });
    }

    get baseUrl() {
        return Env.get("router.baseUrl", location.origin);
    }

    get currentUrl() {
        return Env.get("router.currentUrl", location.origin + location.pathname);
    }

    get currentRoute() {
        return Env.get("router.currentRoute");
    }

    get currentParameters() {
        return Env.get('router.currentParameters');
    }

    get routes() {
        return Env.get("router.routes", {});
    }

    get hasRedirectOnLoginRoute() {
        return !!this.redirectOnLoginRoute;
    }

    get redirectOnLoginRoute() {
        return Env.get("router.redirect_on_login");
    }

    get hasRedirectOnLogoutRoute() {
        return !!this.redirectOnLogoutRoute;
    }

    get redirectOnLogoutRoute() {
        return Env.get("router.redirect_on_logout");
    }

    getOperationForRoute(routeName) {
        return Operation.fromRoute(routeName);
    }

    navigate(routeName, parameters = {}) {
        Promise.resolve()
            .then(() => {
                if (this.onBeforeNavigateCallbacks.length > 0) {
                    return Promise.all(this.onBeforeNavigateCallbacks.map(_callback => _callback(routeName, parameters)))
                }
            })
            .then(() => {

                const operation = this.getOperationForRoute(routeName);

                if (!history
                    || typeof (history.pushState) !== 'function'
                    || !operation.sharesNamespaceWithPage
                    || operation.shouldForceRedirect
                ) {
                    return this.redirectToRoute(routeName, parameters);
                }

                let url = this.route(routeName, parameters);
                if ((url + location.hash) === location.href) {
                    url = url + location.hash;
                }

                Env.set('router.currentParameters', parameters);
                Env.set('router.currentRoute', routeName);
                Env.set('router.currentUrl', url);

                this.current.route = this.currentRoute;
                this.current.parameters = observable.object(this.currentParameters);

                if (this.onNavigateStartCallbacks.length > 0) {
                    this.onNavigateStartCallbacks.forEach((onNavigateStartCallback, index) => {
                        onNavigateStartCallback(routeName, parameters);
                    })
                }
            })

    }

    onBeforeNavigateCallbacks = [];

    onBeforeNavigate(callback) {
        this.onBeforeNavigateCallbacks.push(callback);

        return () => {
            this.removeOnBeforeNavigateListener(callback);
        };
    }

    removeOnBeforeNavigateListener(callback) {
        if (this.onBeforeNavigateCallbacks.length > 0) {
            this.onBeforeNavigateCallbacks.forEach((_callback, index) => {
                if (this.onBeforeNavigateCallbacks[index] === callback) {
                    delete this.onBeforeNavigateCallbacks[index];
                }
            })
        }
    }

    onNavigateStartCallbacks = [];

    onNavigateStart(callback) {
        this.onNavigateStartCallbacks.push(callback);

        return () => {
            this.removeOnNavigateStartListener(callback);
        };
    }

    removeOnNavigateStartListener(callback) {
        if (this.onNavigateStartCallbacks.length > 0) {
            this.onNavigateStartCallbacks.forEach((onNavigateStartCallback, index) => {
                if (this.onNavigateStartCallbacks[index] === callback) {
                    delete this.onNavigateStartCallbacks[index];
                }
            })
        }
    }

    onNavigateCallbacks = [];

    onNavigate(callback) {
        this.onNavigateCallbacks.push(callback);

        return () => {
            this.removeOnNavigateListener(callback);
        };
    }

    removeOnNavigateListener(callback) {
        if (this.onNavigateCallbacks.length > 0) {
            this.onNavigateCallbacks.forEach((onNavigateCallback, index) => {
                if (this.onNavigateCallbacks[index] === callback) {
                    delete this.onNavigateCallbacks[index];
                }
            })
        }
    }

    triggerOnNavigate() {
        if (this.onNavigateCallbacks.length > 0) {
            this.onNavigateCallbacks.forEach((onNavigateCallback, index) => {
                onNavigateCallback(this.currentRoute, this.currentParameters);
            })
        }
    }

    parameter(key) {
        return Env.get('router.currentParameters.' + key, null);
    }

    hasParameter(key) {
        const param = this.parameter(key);

        return (param !== null && typeof param !== "undefined");
    }

    url(path = "", queryParams = null) {
        if (path.length === 0) {
            return this.baseUrl;
        }

        if (path.substr(0, 1) === "/") {
            path = path.substring(1);
        }

        let url = `${this.baseUrl}/${path}`;

        if (queryParams) {

            if (typeof queryParams === "object") {
                queryParams = toQueryString(queryParams);
            }

            if (typeof queryParams === "string" && queryParams !== '') {
                url += `?${queryParams}`;
            }

        }

        return url;
    }

    containsRoute(name) {
        return this.routes[name] != undefined;
    }

    getRoute(name) {
        return this.routes[name];
    }

    route(name, params = null, queryParams = null) {

        let all_params = {
            ...params,
            ...queryParams
        };

        if (!this.containsRoute(name)) {
            if (Env.client()) {
                throw new Error(`The route '${name}' doesn't exist.`);
            } else {
                return "";
            }
        }

        let uri = this.getRoute(name).uri;
        let hash = null;

        if (params && (typeof params === "object")) {
            Object.keys(all_params).forEach((key) => {
                let param = all_params[key];

                if ((param === null || typeof param === "undefined")) {
                    param = '';
                }

                /// Is the current param the HASH part?
                if (param[0] === '#' && (typeof (key) !== 'string' || key === '0' || key === '1' || key === '2' || key === '#')) {
                    hash = param;
                    delete all_params[key];
                    return;
                }

                let regex = new RegExp("\\{" + key + "\\??\\}", "g");
                if (uri.match(regex)) {
                    uri = uri.replace(new RegExp("\\{" + key + "\\??\\}", "g"), param);
                    delete all_params[key];
                }
            });
        }

        // replace remaining optional parameters
        uri = uri.replace(/\{[^}]+\?\}/g, "");

        // replace double slashed resulted from stripping optional parameters
        uri = uri.replace(/\/{2,}/g, "/");

        // replace end slash resulted from stripping optional parameters
        uri = uri.replace(/\/$/g, "");

        /// Append hash if needed
        if (hash !== null) {

            if (hash[0] !== '#') {
                hash = '#' + hash;
            }

            uri = uri + hash;
        }

        return this.url(uri, all_params);
    }

    method(name) {

        if (!this.containsRoute(name)) {
            if (Env.client()) {
                throw new Error(`The route '${name}' doesn't exist.`);
            } else {
                return "";
            }
        }

        let methods = this.getRoute(name).methods;

        if (methods.length > 0) {

            if (methods.indexOf('POST') !== -1) {
                return 'POST';
            }

            return methods[0];
        }

        return null;
    }

    define(pattern, callback) {
        if (typeof pattern === "object") {
            Object.keys(pattern).forEach(routePattern => {
                this.define(routePattern, pattern[routePattern]);
            });

            return this;
        }

        this.definitions.push({pattern, callback});
        return this;
    }

    initialize() {
        // Execute definitions
        if (typeof this.currentRoute === "string") {
            this.definitions.forEach(({pattern, callback}) => {
                const regExp = new RegExp(`^${pattern.replace(/\*/g, ".+")}$`);

                if (this.currentRoute.match(regExp)) {
                    callback();
                }
            });
        }

        return this;
    }

    redirectTo(url) {
        location.href = url;
    }

    redirectToRoute(routeName, parameters = {}) {
        const url = this.route(routeName, parameters);
        this.redirectTo(url);
    }

}

export default RouterHelper;
